import React, { useState } from "react";
import { useNavigate } from 'react-router-dom'
import CommonLayout from '../layouts/CommonLayout'
import TermsModal from '../layouts/TermsModal'
import PrivacyModal from '../layouts/PrivacyModal'
import {
  sendVerificationCode,
  verifyVerificationCode,
  signup,
  checkNameDuplicated,
} from '../../api/AuthApi'
import { AlertModal } from "../layouts/AlertModal";

const SignupPage = () => {
  const navigate = useNavigate()

  const [signupForm, setSignupForm] = useState({
    phone: '',
    verificationCode: '',
    nickname: '',
    password: '',
    confirmPassword: '',
    agreeTerms: false,
    agreePrivacy: false,
  })

  const [isPhoneVerified, setIsPhoneVerified] = useState(false)
  const [isCodeSent, setIsCodeSent] = useState(false)
  const [isNicknameVerified, setIsNicknameVerified] = useState(false)

  // AlertModal 상태 관리 (title 제거)
  const [alertState, setAlertState] = useState({
    isVisible: false,
    message: '',
    callback: null,
  })

  // 약관 모달 상태 관리
  const [termsModalState, setTermsModalState] = useState({
    isVisible: false,
  })

  const [privacyModalState, setPrivacyModalState] = useState({
    isVisible: false,
  })

  // API 에러 메시지 추출 헬퍼 함수
  const getErrorMessage = (error) => {
    if (error.response && error.response.data && error.response.data.message) {
      return error.response.data.message
    }
    return '오류가 발생했습니다. 잠시 후 다시 시도해주세요.'
  }

  const handleSendCode = async () => {
    try {
      await sendVerificationCode(signupForm.phone)
      setAlertState({ isVisible: true, message: '인증번호가 발송되었습니다.' })
      setIsCodeSent(true)
    } catch (error) {
      setAlertState({ isVisible: true, message: getErrorMessage(error) })
    }
  }

  const handleVerifyCode = async () => {
    try {
      await verifyVerificationCode(signupForm.phone, signupForm.verificationCode)
      setAlertState({ isVisible: true, message: '인증이 완료되었습니다.' })
      setIsPhoneVerified(true)
    } catch (error) {
      setAlertState({ isVisible: true, message: getErrorMessage(error) })
    }
  }

  const handleNicknameCheck = async () => {
    if (!signupForm.nickname.trim()) {
      setAlertState({ isVisible: true, message: '닉네임을 입력해주세요.' })
      return
    }

    if (signupForm.nickname.length > 10) {
      setAlertState({ isVisible: true, message: '닉네임은 10자 이하여야 합니다.' })
      return
    }

    try {
      const response = await checkNameDuplicated(signupForm.nickname)
      if (response.duplicated) {
        setAlertState({ isVisible: true, message: '이미 사용 중인 닉네임입니다.' })
        setIsNicknameVerified(false)
      } else {
        setAlertState({ isVisible: true, message: '사용 가능한 닉네임입니다.' })
        setIsNicknameVerified(true)
      }
    } catch (error) {
      setAlertState({ isVisible: true, message: getErrorMessage(error) })
    }
  }

  const handleSignup = async () => {
    if (!isPhoneVerified) {
      setAlertState({ isVisible: true, message: '휴대폰 번호 인증을 완료해주세요' })
      return
    }
    if (!isNicknameVerified) {
      setAlertState({ isVisible: true, message: '닉네임 중복 검사를 완료해주세요' })
      return
    }
    if (signupForm.nickname.length > 10) {
      setAlertState({ isVisible: true, message: '닉네임은 10자 이하여야 합니다.' })
      return
    }
    if (signupForm.password.length < 6) {
      setAlertState({ isVisible: true, message: '비밀번호는 6자 이상이어야 합니다.' })
      return
    }
    if (signupForm.password !== signupForm.confirmPassword) {
      setAlertState({ isVisible: true, message: '비밀번호가 일치하지 않습니다.' })
      return
    }
    if (!signupForm.agreeTerms || !signupForm.agreePrivacy) {
      setAlertState({ isVisible: true, message: '이용약관과 개인정보처리방침에 모두 동의해주세요.' })
      return
    }

    try {
      await signup(signupForm.nickname, signupForm.phone, signupForm.password)
      setAlertState({
        isVisible: true,
        message: '회원가입이 완료되었습니다.',
        callback: () => navigate('/app')
      })
    } catch (error) {
      setAlertState({ isVisible: true, message: getErrorMessage(error) })
    }
  }

  // 전체 동의 처리 함수
  const handleAgreeAll = (checked) => {
    setSignupForm({
      ...signupForm,
      agreeTerms: checked,
      agreePrivacy: checked,
    })
  }

  // 개별 체크박스 변경 시 전체 동의 상태 업데이트
  const handleIndividualCheck = (field, checked) => {
    const newForm = { ...signupForm, [field]: checked }
    setSignupForm(newForm)
  }

  // 약관 모달 열기 함수들
  const openTermsModal = () => {
    setTermsModalState({ isVisible: true })
  }

  const openPrivacyModal = () => {
    setPrivacyModalState({ isVisible: true })
  }

  // 모든 필드가 유효한지 확인
  const isFormValid = () => {
    return (
        isPhoneVerified &&
        isNicknameVerified &&
        signupForm.nickname.trim().length > 0 &&
        signupForm.nickname.length <= 10 &&
        signupForm.password.length >= 6 &&
        signupForm.password === signupForm.confirmPassword &&
        signupForm.agreeTerms &&
        signupForm.agreePrivacy
    )
  }

  // 전체 동의 상태 확인
  const isAllAgreed = signupForm.agreeTerms && signupForm.agreePrivacy

  return (
      <CommonLayout title="회원가입">
        <AlertModal
            isVisible={alertState.isVisible}
            message={alertState.message}
            callback={alertState.callback}
            onClose={() => setAlertState({ isVisible: false, message: '', callback: null })}
        />

        <TermsModal
            isVisible={termsModalState.isVisible}
            onClose={() => setTermsModalState({ isVisible: false })}
        />

        <PrivacyModal
            isVisible={privacyModalState.isVisible}
            onClose={() => setPrivacyModalState({ isVisible: false })}
        />

        <div className="space-y-4 p-4">
          {/* 휴대폰 번호 입력 필드 */}
          <div className="flex items-center space-x-2">
            <div className="text-sm font-semibold">휴대폰 번호 인증</div>
            {isPhoneVerified && (
                <div className="flex items-center space-x-1">
                  <div className="w-2 h-2 bg-green-500 rounded-full"></div>
                  <span className="text-sm text-green-600 font-medium">
                인증완료
              </span>
                </div>
            )}
          </div>
          <div className="flex space-x-2">
            <input
                type="text"
                placeholder="휴대폰 번호 (- 없이 입력)"
                value={signupForm.phone}
                onChange={(e) =>
                    setSignupForm({ ...signupForm, phone: e.target.value })
                }
                className={`${
                    isPhoneVerified ? 'w-full' : 'w-3/5'
                } px-2 py-3 border rounded-lg ${
                    isPhoneVerified
                        ? 'border-green-300 bg-green-50'
                        : 'border-gray-300'
                }`}
                minLength="11"
                maxLength="11"
                disabled={isPhoneVerified}
            />
            {!isPhoneVerified && (
                <button
                    onClick={handleSendCode}
                    className="w-2/5 px-2 py-3 bg-gray-200 text-gray-700 rounded-lg whitespace-nowrap"
                >
                  {isCodeSent ? '인증번호 재전송' : '인증번호 전송'}
                </button>
            )}
          </div>

          {/* 인증번호 입력 필드 */}
          <div className="flex space-x-2">
            <input
                type="text"
                placeholder="인증번호 6자리"
                value={signupForm.verificationCode}
                onChange={(e) =>
                    setSignupForm({
                      ...signupForm,
                      verificationCode: e.target.value,
                    })
                }
                className={`${
                    isPhoneVerified
                        ? 'w-full border-green-300 bg-green-50'
                        : 'w-3/5 border-gray-300'
                } px-3 py-3 border rounded-lg`}
                maxLength="6"
                disabled={isPhoneVerified}
            />
            {!isPhoneVerified && (
                <button
                    onClick={handleVerifyCode}
                    className="w-2/5 px-1 py-3 bg-gray-200 text-gray-700 rounded-lg whitespace-nowrap"
                >
                  인증번호 확인
                </button>
            )}
          </div>
          {/* 닉네임 입력 필드 */}
          <div className="flex items-center space-x-2">
            <div className="text-sm font-semibold">닉네임</div>
            {isNicknameVerified && (
                <div className="flex items-center space-x-1">
                  <div className="w-2 h-2 bg-green-500 rounded-full"></div>
                  <span className="text-sm text-green-600 font-medium">
                사용가능
              </span>
                </div>
            )}
          </div>
          <div className="flex space-x-2">
            <input
                type="text"
                maxLength="10"
                placeholder="닉네임 (최대 10자)"
                value={signupForm.nickname}
                onChange={(e) => {
                  setSignupForm({ ...signupForm, nickname: e.target.value })
                  setIsNicknameVerified(false) // 닉네임 변경 시 검사 상태 초기화
                }}
                className={`flex-1 px-4 py-3 border rounded-lg ${
                    isNicknameVerified
                        ? 'border-green-300 bg-green-50'
                        : 'border-gray-300'
                }`}
            />
            <button
                onClick={handleNicknameCheck}
                className="px-4 py-3 bg-gray-200 text-gray-700 rounded-lg whitespace-nowrap"
                disabled={!signupForm.nickname.trim()}
            >
              중복 검사
            </button>
          </div>
          <div className="text-xs text-amber-600 mt-2">
            닉네임은 30일 후 변경할 수 있습니다. 신중히 입력해주세요.
          </div>

          {/* 비밀번호 입력 필드 */}
          <div className="text-sm font-semibold">비밀번호</div>
          <div>
            <input
                type="password"
                placeholder="비밀번호 (6자 이상, 16자 이하)"
                value={signupForm.password}
                onChange={(e) =>
                    setSignupForm({ ...signupForm, password: e.target.value })
                }
                minLength="6"
                maxLength="16"
                className="w-full px-4 py-3 border border-gray-300 rounded-lg"
            />
          </div>

          {/* 비밀번호 확인 필드 */}
          <div className="text-sm font-semibold">비밀번호 확인</div>
          <div>
            <input
                type="password"
                placeholder="비밀번호 확인"
                value={signupForm.confirmPassword}
                onChange={(e) =>
                    setSignupForm({ ...signupForm, confirmPassword: e.target.value })
                }
                minLength="6"
                maxLength="16"
                className="w-full px-4 py-3 border border-gray-300 rounded-lg"
            />
          </div>

          {/* 이용약관 및 개인정보처리방침 박스 */}
          <div className="text-sm font-semibold">이용약관 및 개인정보처리방침</div>
          <div className="space-y-3 border border-gray-300 rounded-lg p-4 bg-gray-50">
            {/* 약관 보기 버튼들 */}
            <div className="flex space-x-2">
              <button 
                onClick={openTermsModal}
                className="flex-1 py-2 text-blue-600 text-sm border border-blue-300 rounded-lg hover:bg-blue-50"
              >
                이용약관 보기
              </button>
              <button 
                onClick={openPrivacyModal}
                className="flex-1 py-2 text-blue-600 text-sm border border-blue-300 rounded-lg hover:bg-blue-50"
              >
                개인정보처리방침 보기
              </button>
            </div>
            
            {/* 전체 동의 체크박스 */}
            <div className="border-t pt-3">
              <label className="flex items-center space-x-2">
                <input
                    type="checkbox"
                    className="w-4 h-4"
                    checked={isAllAgreed}
                    onChange={(e) => handleAgreeAll(e.target.checked)}
                />
                <span className="text-sm font-medium text-gray-700">
                  전체 항목에 동의합니다
                </span>
              </label>
            </div>

            {/* 개별 체크박스들 */}
            <div className="space-y-2">
              <label className="flex items-center space-x-2">
                <input
                    type="checkbox"
                    className="w-4 h-4"
                    checked={signupForm.agreeTerms}
                    onChange={(e) => handleIndividualCheck('agreeTerms', e.target.checked)}
                />
                <span className="text-sm text-gray-700">
                  이용약관에 동의합니다
                </span>
              </label>
              <label className="flex items-center space-x-2">
                <input
                    type="checkbox"
                    className="w-4 h-4"
                    checked={signupForm.agreePrivacy}
                    onChange={(e) => handleIndividualCheck('agreePrivacy', e.target.checked)}
                />
                <span className="text-sm text-gray-700">
                  개인정보처리방침에 동의합니다
                </span>
              </label>
            </div>
          </div>

          <button
              onClick={handleSignup}
              className={`w-full py-3 rounded-lg ${
                  isFormValid()
                      ? 'bg-amber-500 text-white active:bg-amber-600'
                      : 'bg-gray-300 text-gray-500 cursor-not-allowed'
              }`}
              disabled={!isFormValid()}
          >
            회원가입
          </button>
        </div>
      </CommonLayout>
  )
}

export default SignupPage
